import React, { useState } from "react";
import {
  Box,
  Slider,
  TextField,
  Typography,
  Button,
} from "@mui/material";

const BoxShadowGenerator = ({
  toggleDrawer,
  editItem,
  handleSave,
  headerItemData,
  handleApi,
  isMobile,
}) => {
  let shadow = headerItemData?.customHeader?.customHeader?.customizeHeader[
    editItem.prevItem
  ].shadow ?? {
    horizontal: "",
    vertical: "",
    blur: "",
    spread: "",
    opacity: "",
    color: "",
    inset: false,
  };

  if (isMobile) {
    if (editItem?.prevItem === "search") {
      shadow = headerItemData?.customHeader?.customHeader?.customizeHeader[
        "mobileSearch"
      ].shadow ?? {
        horizontal: "",
        vertical: "",
        blur: "",
        spread: "",
        opacity: "",
        color: "",
        inset: false,
      };
    } else {
      shadow = headerItemData?.customHeader?.customHeader?.customizeHeader[
        "mobileItems"
      ].shadow ?? {
        horizontal: "",
        vertical: "",
        blur: "",
        spread: "",
        opacity: "",
        color: "",
        inset: false,
      };
    }
  }

  const [shadowValues, setShadowValues] = useState({ ...shadow });

  const [isToggled, setIsToggled] = useState(shadow.inset);

  const handleToggle = () => {
    setIsToggled((prevState) => !prevState);
    handleShadowSave("inset", !isToggled);
  };

  // Inline CSS for the button and container
  const toggleStyles = {
    position: "relative",
    width: "50px",
    height: "15px",
    backgroundColor: isToggled ? "#4caf50" : "#ccc",
    borderRadius: "50px",
    cursor: "pointer",
    transition: "background-color 0.3s",
  };

  const circleStyles = {
    position: "absolute",
    top: "-3px",
    left: isToggled ? "30px" : 0,
    width: "21px",
    height: "21px",
    backgroundColor: "white",
    borderRadius: "50%",
    transition: "all 0.2s linear",
    boxShadow: "0 0 4px 1px rgba(0,0,0,.4)",
  };



  const generateBoxShadow = () => {
    // Convert hex color to RGB
    const hexToRgb = (hex) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result
        ? {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16),
        }
        : null;
    };

    const rgb = hexToRgb(shadowValues?.color || "#6f747d");
    const opacity = shadowValues?.opacity / 100; // Convert percentage to decimal

    // Generate rgba color with opacity
    const rgba = rgb
      ? `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${opacity})`
      : `rgba(0, 0, 0, ${opacity})`; // Fallback if color parsing fails
    // console.log(rgba, shadowValues.color);
    const shade = `${shadowValues.inset ? "inset " : ""}${shadowValues.horizontal
      }px ${shadowValues.vertical}px ${shadowValues.blur}px ${shadowValues.spread
      }px ${rgba}`;

    return shade;
  };

  const handleShadowSave = (key, value) => {
    let updatedShadowValues = shadowValues;
    setShadowValues((prev) => ({ ...prev, [key]: value }));
    updatedShadowValues[key] = value;
    // console.log({ updatedShadowValues });
    handleSave(editItem?.prevItem, "shadow", updatedShadowValues);
  };

  const handleColorChange = (event) => {
    handleShadowSave("color", event.target.value);
  };



  return (
    <Box
      sx={{
        borderRadius: "5px",
        width: "100%",
        height: "100vh",
        margin: "0 auto",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "space-between",
        backgroundColor: "#fff",
        overflowY: "scroll",
        overflowX: "hidden",
        "&::-webkit-scrollbar": {
          width: "6px",
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: "#f4f4f4",
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "#888",
          borderRadius: "10px",
        },
        "&::-webkit-scrollbar-thumb:hover": {
          backgroundColor: "#555",
        },
      }}
    >
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            backgroundColor: "#333",
            color: "#fff",
            padding: "5px",
            marginBottom: "10px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Typography
            variant="h6"
            sx={{ fontWeight: "bold", fontSize: "16px" }}
          >
            Edit Shadow
          </Typography>
          {editItem?.item !== "bgColor" && (
            <Button
              variant="text"
              sx={{ fontWeight: "bold", color: "#fff" }}
              onClick={() => {
                toggleDrawer(true, editItem?.index, editItem?.prevItem);
              }}
            >
              Back
            </Button>
          )}
        </Box>

        {/* Box Shadow Preview */}
        <Box
          sx={{
            width: "80%",
            height: "100px",
            backgroundColor: "#333",
            borderRadius: "8px",
            margin: "30px 0",
            boxShadow: generateBoxShadow(),
          }}
        ></Box>

        {/* Slider Controls */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
            padding: "10px 20px",
            width: "100%",
          }}
        >
          {/* Horizontal Offset */}
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography variant="body2" color="text.secondary">
              Horizontal Offset
            </Typography>
            <Slider
              value={shadowValues?.horizontal}
              onChange={(_, value) => handleShadowSave("horizontal", value)}
              min={-50}
              max={50}
              step={1}
              sx={{ marginTop: "8px", color: "#333" }}
            />
          </Box>

          {/* Vertical Offset */}
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography variant="body2" color="text.secondary">
              Vertical Offset
            </Typography>
            <Slider
              value={shadowValues?.vertical}
              onChange={(_, value) => handleShadowSave("vertical", value)}
              min={-50}
              max={50}
              step={1}
              sx={{ marginTop: "8px", color: "#333" }}
            />
          </Box>

          {/* Blur Radius */}
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography variant="body2" color="text.secondary">
              Blur Radius
            </Typography>
            <Slider
              value={shadowValues?.blur}
              onChange={(_, value) => handleShadowSave("blur", value)}
              min={0}
              max={100}
              step={1}
              sx={{ marginTop: "8px", color: "#333" }}
            />
          </Box>

          {/* Spread Radius */}
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography variant="body2" color="text.secondary">
              Spread Radius
            </Typography>
            <Slider
              value={shadowValues?.spread}
              onChange={(_, value) => handleShadowSave("spread", value)}
              min={-50}
              max={50}
              step={1}
              sx={{ marginTop: "8px", color: "#333" }}
            />
          </Box>

          {/* Opacity */}
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography variant="body2" color="text.secondary">
              Opacity
            </Typography>
            <Slider
              value={shadowValues?.opacity}
              onChange={(_, value) => handleShadowSave("opacity", value)}
              min={0}
              max={100}
              step={1}
              sx={{ marginTop: "8px", color: "#333" }}
            />
          </Box>
        </Box>

        {/* Color Picker (Input type="color") */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: "24px",
            padding: "10px",
            width: "100%",
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Shadow Color
          </Typography>
          <TextField
            type="color"
            value={shadowValues?.color}
            onChange={handleColorChange}
            sx={{
              width: "80px",
              height: "20px",
              padding: 0,
              borderRadius: "50%",
              "& .MuiInputBase-input": {
                cursor: "pointer",
              },
            }}
          />
        </Box>

        {/* Inset or Offset Shadow Toggle */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            columnGap: "10px",
            alignSelf: "start",
            padding: "10px",
          }}
        >
          <Box sx={toggleStyles} onClick={handleToggle}>
            <Box sx={circleStyles} />
          </Box>

          <Typography
            variant="body2"
            sx={{ fontSize: "15px" }}
            color="text.secondary"
          >
            Inset Shadow
          </Typography>
        </Box>

        <Button
          variant="contained"
          color="success"
          fullWidth
          sx={{
            fontSize: "12px",
            padding: "6px",
            width: "90%",
            borderRadius: "50px",
            margin: "30px 0 20px 0",
          }}
          onClick={handleApi}
        >
          Save
        </Button>
      </Box>
    </Box>
  );
};

export default BoxShadowGenerator;
